(function () {
  const ROOT_SELECTOR = '[data-fb-quick-finder]';
  const CHIP_SELECTOR = '[data-fb-qf-chip]';
  const PANEL_SELECTOR = '[data-fb-qf-panel]';
  const MOBILE_QUERY = '(max-width: 749px)';

  function clean(value, fallback) {
    return String(value || fallback || '').trim();
  }

  function readPrefill(url) {
    try {
      const parsed = new URL(url, window.location.origin);
      return {
        fb_use_case: parsed.searchParams.get('fb_use_case') || '',
        fb_duration: parsed.searchParams.get('fb_duration') || '',
        fb_caffeine: parsed.searchParams.get('fb_caffeine') || '',
        fb_priority: parsed.searchParams.get('fb_priority') || '',
        fb_source: parsed.searchParams.get('fb_source') || '',
      };
    } catch (error) {
      return {};
    }
  }

  function deviceType() {
    return window.matchMedia(MOBILE_QUERY).matches ? 'mobile' : 'desktop';
  }

  function pushEvent(root, name, detail) {
    const payload = {
      event: name,
      section_id: root.dataset.sectionId || '',
      device_type: deviceType(),
      page_location: window.location.href,
      ...detail,
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    root.dispatchEvent(new CustomEvent(`fb:${name}`, { bubbles: true, detail: payload }));
  }

  function initQuickFinder(root) {
    if (root.dataset.fbQuickFinderInitialized === 'true') return;
    root.dataset.fbQuickFinderInitialized = 'true';

    const chips = Array.from(root.querySelectorAll(CHIP_SELECTOR));
    const panels = Array.from(root.querySelectorAll(PANEL_SELECTOR));
    const chipsWrap = root.querySelector('[data-fb-qf-chips]');
    const status = root.querySelector('[data-fb-qf-status]');
    if (!chips.length || !panels.length) return;

    let viewed = false;
    let currentPanel = panels.find((panel) => !panel.hidden) || panels[0];

    function panelForChip(chip) {
      return panels.find((panel) => panel.dataset.fbQfPanel === chip.dataset.fbQfChip) || null;
    }

    function activeChipForPanel(panel) {
      return chips.find((chip) => chip.dataset.fbQfChip === panel.dataset.fbQfPanel) || null;
    }

    function chipPosition(chip) {
      const index = chips.indexOf(chip);
      return index >= 0 ? index + 1 : '';
    }

    function enhanceTabSemantics() {
      chipsWrap?.setAttribute('role', 'tablist');
      panels.forEach((panel) => {
        const chip = activeChipForPanel(panel);
        panel.setAttribute('role', 'tabpanel');
        if (chip?.id) panel.setAttribute('aria-labelledby', chip.id);
      });
      chips.forEach((chip) => {
        const panel = panelForChip(chip);
        const isActive = panel === currentPanel;
        chip.setAttribute('role', 'tab');
        if (panel?.id) chip.setAttribute('aria-controls', panel.id);
        chip.setAttribute('aria-selected', String(isActive));
        chip.setAttribute('tabindex', isActive ? '0' : '-1');
      });
    }

    function productPayload(panel, productCard) {
      const chip = activeChipForPanel(panel);
      const cardPosition = productCard ? Array.from(panel.querySelectorAll('product-card')).indexOf(productCard) + 1 : 0;
      return {
        chip_id: chip?.dataset.fbQfChip || panel.dataset.fbQfPanel || '',
        chip_label: clean(chip?.dataset.chipLabel, chip?.textContent),
        analytics_key: clean(panel.dataset.analyticsKey, chip?.dataset.analyticsKey),
        result_title: clean(panel.dataset.panelTitle),
        card_position: cardPosition > 0 ? cardPosition : '',
        product_id: productCard?.dataset.productId || '',
        product_handle: productCard?.dataset.productHandle || '',
        product_title: productCard?.dataset.productTitle || '',
        variant_id: productCard?.dataset.variantId || '',
        variant_sku: productCard?.dataset.variantSku || '',
        card_context: productCard?.dataset.cardContext || '',
      };
    }

    function pushResultView(panel, chip) {
      const href = chip?.getAttribute('href') || '';
      pushEvent(root, 'quickfinder_result_view', {
        chip_id: chip?.dataset.fbQfChip || '',
        chip_label: clean(chip?.dataset.chipLabel, chip?.textContent),
        chip_position: chip ? chipPosition(chip) : '',
        analytics_key: clean(chip?.dataset.analyticsKey, panel.dataset.analyticsKey),
        result_title: clean(panel.dataset.panelTitle),
        visible_product_count: panel.querySelectorAll('product-card').length,
        ...readPrefill(href),
      });
    }

    function revealPanel(panel) {
      if (!window.matchMedia(MOBILE_QUERY).matches) return;
      window.requestAnimationFrame(() => {
        panel.scrollIntoView({
          block: 'start',
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      });
    }

    function showPanel(nextPanel, sourceChip, options) {
      if (!nextPanel) return;
      if (nextPanel === currentPanel) {
        if (options?.reveal) revealPanel(nextPanel);
        return;
      }

      panels.forEach((panel) => {
        panel.hidden = panel !== nextPanel;
      });

      chips.forEach((chip) => {
        const isActive = chip === sourceChip;
        chip.classList.toggle('is-active', isActive);
        chip.setAttribute('aria-selected', String(isActive));
        chip.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      currentPanel = nextPanel;
      if (status) status.textContent = `${clean(nextPanel.dataset.panelTitle)} angezeigt`;
      pushResultView(nextPanel, sourceChip);
      if (options?.reveal) revealPanel(nextPanel);
    }

    enhanceTabSemantics();

    chips.forEach((chip, index) => {
      chip.addEventListener('click', (event) => {
        if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (event.button !== 0) return;

        const panel = panelForChip(chip);
        if (!panel) return;

        event.preventDefault();
        pushEvent(root, 'quickfinder_chip_click', {
          chip_id: chip.dataset.fbQfChip || '',
          chip_label: clean(chip.dataset.chipLabel, chip.textContent),
          chip_position: chipPosition(chip),
          analytics_key: clean(chip.dataset.analyticsKey),
          target_url: chip.href || '',
          interaction_type: 'click',
          ...readPrefill(chip.href || ''),
        });
        showPanel(panel, chip, { reveal: true });
      });

      chip.addEventListener('keydown', (event) => {
        let nextIndex = -1;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          nextIndex = (index + 1) % chips.length;
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          nextIndex = (index - 1 + chips.length) % chips.length;
        } else if (event.key === 'Home') {
          nextIndex = 0;
        } else if (event.key === 'End') {
          nextIndex = chips.length - 1;
        } else if (event.key === ' ' || event.key === 'Enter') {
          const panel = panelForChip(chip);
          if (!panel) return;
          event.preventDefault();
          showPanel(panel, chip, { reveal: true });
          return;
        } else {
          return;
        }

        event.preventDefault();
        const nextChip = chips[nextIndex];
        const nextPanel = panelForChip(nextChip);
        nextChip.focus();
        showPanel(nextPanel, nextChip);
      });
    });

    panels.forEach((panel) => {
      panel.addEventListener('click', (event) => {
        const target = event.target instanceof Element ? event.target : null;
        const quickAddButton = target?.closest('.quick-add__button') || null;
        if (quickAddButton) {
          const productCard = quickAddButton.closest('product-card');
          if (!productCard) return;

          pushEvent(root, 'quickfinder_quick_add_click', {
            ...productPayload(panel, productCard),
            cta_type: 'quick_add',
          });
          return;
        }

        const link = target?.closest('a') || null;
        if (!link) return;

        const isFinderLink = link.hasAttribute('data-fb-qf-finder-link');
        const productCard = link.closest('product-card');

        pushEvent(root, isFinderLink ? 'quickfinder_full_finder_click' : 'quickfinder_card_click', {
          ...productPayload(panel, productCard),
          target_url: link.href || '',
          cta_type: isFinderLink ? 'finder_link' : 'product_link',
          ...readPrefill(link.href || ''),
        });
      });
    });

    root.addEventListener('cart:update', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const productForm = target?.matches('product-form-component')
        ? target
        : target?.closest('product-form-component');
      const panel = productForm?.closest(PANEL_SELECTOR);
      if (!panel) return;

      const productCard = productForm.closest('product-card');
      if (!productCard) return;

      const data = event.detail?.data || {};
      pushEvent(root, data.didError ? 'quickfinder_quick_add_error' : 'quickfinder_quick_add_success', {
        ...productPayload(panel, productCard),
        cta_type: 'quick_add',
        item_count: data.itemCount || '',
        source_id: event.detail?.sourceId || '',
        cart_source: data.source || '',
      });
    });

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (viewed || !entry.isIntersecting) return;

            viewed = true;
            observer.disconnect();
            const chip = activeChipForPanel(currentPanel);
            pushEvent(root, 'quickfinder_view', {
              chip_count: chips.length,
              active_chip_id: chip?.dataset.fbQfChip || '',
              active_chip_label: clean(chip?.dataset.chipLabel, chip?.textContent),
            });
            pushResultView(currentPanel, chip);
          });
        },
        { threshold: 0.35 }
      );
      observer.observe(root);
    } else {
      const chip = activeChipForPanel(currentPanel);
      pushEvent(root, 'quickfinder_view', {
        chip_count: chips.length,
        active_chip_id: chip?.dataset.fbQfChip || '',
        active_chip_label: clean(chip?.dataset.chipLabel, chip?.textContent),
      });
      pushResultView(currentPanel, chip);
    }
  }

  function initAll() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(initQuickFinder);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }
})();
